import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector';
import type { Route, RoutePoint } from '../types/storage';
import { reverseGeocode } from '../utils/addressResolver';
import { geocodeResultToAddressFields } from '../utils/geocodingConverter';

export interface GPXData {
  name: string;
  waypoints: { lat: number; lng: number }[];
  metadata: {
    filename: string;
    trackCount: number;
    totalPoints: number;
    deviceInfo?: any;
    timestamps?: {
      start?: string;
      end?: string;
    };
    bounds?: {
      minLat: number;
      maxLat: number;
      minLng: number;
      maxLng: number;
    };
  };
}

export interface RouteProcessingResult {
  success: boolean;
  route?: Route;
  error?: string;
}

export class GPXProcessor {
  /**
   * Calculate optimal waypoint count based on input file size
   * With POST support, we can handle much larger coordinate sets
   * Reduced limits to minimize API calls for uploaded routes
   */
  private calculateOptimalWaypointCount(inputWaypoints: number): number {
    // Conservative limits to minimize Nominatim API calls for uploaded routes
    if (inputWaypoints <= 50) return Math.min(inputWaypoints, 15);        // Small files: keep essential points
    if (inputWaypoints <= 200) return 20;                                 // Medium files: moderate detail  
    if (inputWaypoints <= 500) return 25;                                 // Large files: key points only
    if (inputWaypoints <= 1000) return 30;                                // Very large: essential waypoints
    if (inputWaypoints <= 2000) return 35;                                // Massive files: minimal set
    return 40;                                                             // Extreme files: absolute maximum
  }

  /**
   * Extract significant waypoints from GPX track points using advanced algorithms
   * Handles hundreds to thousands of points efficiently
   */
  private extractKeyWaypoints(waypoints: { lat: number; lng: number }[], maxPoints: number = 25): { lat: number; lng: number }[] {
    if (waypoints.length <= maxPoints) {
      return waypoints;
    }

    // For very large files, use multi-stage reduction
    if (waypoints.length > 1000) {
      return this.multiStageReduction(waypoints, maxPoints);
    } else if (waypoints.length > 200) {
      return this.douglasPeuckerReduction(waypoints, maxPoints);
    } else {
      return this.simpleReduction(waypoints, maxPoints);
    }
  }

  /**
   * Multi-stage reduction for very large GPX files (1000+ points)
   * Stage 1: Distance-based thinning
   * Stage 2: Douglas-Peucker algorithm
   * Stage 3: Direction change detection
   */
  private multiStageReduction(waypoints: { lat: number; lng: number }[], maxPoints: number): { lat: number; lng: number }[] {
    
    // Stage 1: Distance-based thinning (reduce to ~200 points)
    const minDistance = this.calculateOptimalMinDistance(waypoints, 200);
    let stage1Result = this.distanceBasedThinning(waypoints, minDistance);
    
    // Stage 2: Douglas-Peucker if still too many
    if (stage1Result.length > maxPoints * 2) {
      stage1Result = this.douglasPeuckerReduction(stage1Result, maxPoints * 2);
    }
    
    // Stage 3: Final selection with direction changes
    const finalResult = this.directionChangeReduction(stage1Result, maxPoints);
    
    return finalResult;
  }

  /**
   * Distance-based thinning: remove points too close together
   */
  private distanceBasedThinning(waypoints: { lat: number; lng: number }[], minDistanceKm: number): { lat: number; lng: number }[] {
    if (waypoints.length === 0) return [];
    
    const result: { lat: number; lng: number }[] = [waypoints[0]]; // Always keep first
    
    for (let i = 1; i < waypoints.length; i++) {
      const lastKept = result[result.length - 1];
      const distance = this.calculateDistanceBetweenPoints(lastKept.lat, lastKept.lng, waypoints[i].lat, waypoints[i].lng);
      
      if (distance >= minDistanceKm || i === waypoints.length - 1) { // Always keep last
        result.push(waypoints[i]);
      }
    }
    
    return result;
  }

  /**
   * Calculate optimal minimum distance based on total route length and target points
   */
  private calculateOptimalMinDistance(waypoints: { lat: number; lng: number }[], targetPoints: number): number {
    if (waypoints.length < 2) return 0;
    
    // Calculate total route distance
    let totalDistance = 0;
    for (let i = 1; i < waypoints.length; i++) {
      totalDistance += this.calculateDistanceBetweenPoints(
        waypoints[i-1].lat, waypoints[i-1].lng,
        waypoints[i].lat, waypoints[i].lng
      );
    }
    
    // Aim for points every X km based on total distance
    const minDistance = totalDistance / targetPoints;
    return Math.max(minDistance, 0.05); // Minimum 50m between points
  }

  /**
   * Simplified Douglas-Peucker algorithm for line simplification
   */
  private douglasPeuckerReduction(waypoints: { lat: number; lng: number }[], maxPoints: number): { lat: number; lng: number }[] {
    if (waypoints.length <= maxPoints) return waypoints;
    
    // Start with a tolerance and increase until we get desired point count
    let tolerance = 0.00001; // ~1 meter
    let result = waypoints;
    
    while (result.length > maxPoints && tolerance < 0.01) { // Max ~1km tolerance
      result = this.douglasPeuckerSimplify(waypoints, tolerance);
      tolerance *= 2;
    }
    
    // If still too many points, fall back to simple reduction
    if (result.length > maxPoints) {
      result = this.simpleReduction(result, maxPoints);
    }
    
    return result;
  }

  /**
   * Douglas-Peucker line simplification algorithm
   */
  private douglasPeuckerSimplify(waypoints: { lat: number; lng: number }[], tolerance: number): { lat: number; lng: number }[] {
    if (waypoints.length <= 2) return waypoints;
    
    // Find point with maximum distance from line between start and end
    let maxDistance = 0;
    let maxIndex = 0;
    
    const start = waypoints[0];
    const end = waypoints[waypoints.length - 1];
    
    for (let i = 1; i < waypoints.length - 1; i++) {
      const distance = this.perpendicularDistance(waypoints[i], start, end);
      if (distance > maxDistance) {
        maxDistance = distance;
        maxIndex = i;
      }
    }
    
    // If max distance is greater than tolerance, recursively simplify
    if (maxDistance > tolerance) {
      const left = this.douglasPeuckerSimplify(waypoints.slice(0, maxIndex + 1), tolerance);
      const right = this.douglasPeuckerSimplify(waypoints.slice(maxIndex), tolerance);
      
      // Combine results (remove duplicate middle point)
      return left.slice(0, -1).concat(right);
    } else {
      // All points are within tolerance, return just start and end
      return [start, end];
    }
  }

  /**
   * Calculate perpendicular distance from point to line
   */
  private perpendicularDistance(point: { lat: number; lng: number }, lineStart: { lat: number; lng: number }, lineEnd: { lat: number; lng: number }): number {
    const A = point.lat - lineStart.lat;
    const B = point.lng - lineStart.lng;
    const C = lineEnd.lat - lineStart.lat;
    const D = lineEnd.lng - lineStart.lng;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) return Math.sqrt(A * A + B * B);
    
    const param = dot / lenSq;
    
    let closestLat, closestLng;
    
    if (param < 0) {
      closestLat = lineStart.lat;
      closestLng = lineStart.lng;
    } else if (param > 1) {
      closestLat = lineEnd.lat;
      closestLng = lineEnd.lng;
    } else {
      closestLat = lineStart.lat + param * C;
      closestLng = lineStart.lng + param * D;
    }
    
    const dx = point.lat - closestLat;
    const dy = point.lng - closestLng;
    
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Direction-based reduction focusing on significant turns
   */
  private directionChangeReduction(waypoints: { lat: number; lng: number }[], maxPoints: number): { lat: number; lng: number }[] {
    if (waypoints.length <= maxPoints) return waypoints;
    
    const result: { lat: number; lng: number }[] = [waypoints[0]]; // Always keep first
    
    // Calculate direction changes for all points
    const directionChanges: { index: number; change: number }[] = [];
    
    for (let i = 1; i < waypoints.length - 1; i++) {
      if (this.isSignificantWaypoint(waypoints, i)) {
        const bearing1 = this.calculateBearing(waypoints[i-1].lat, waypoints[i-1].lng, waypoints[i].lat, waypoints[i].lng);
        const bearing2 = this.calculateBearing(waypoints[i].lat, waypoints[i].lng, waypoints[i+1].lat, waypoints[i+1].lng);
        const change = Math.min(Math.abs(bearing1 - bearing2), 360 - Math.abs(bearing1 - bearing2));
        
        directionChanges.push({ index: i, change });
      }
    }
    
    // Sort by direction change magnitude and take the most significant ones
    directionChanges.sort((a, b) => b.change - a.change);
    const keepIndices = directionChanges
      .slice(0, maxPoints - 2) // Reserve space for start/end
      .map(item => item.index)
      .sort((a, b) => a - b);
    
    // Add selected points
    for (const index of keepIndices) {
      result.push(waypoints[index]);
    }
    
    // Always keep last
    result.push(waypoints[waypoints.length - 1]);
    
    return result;
  }

  /**
   * Simple reduction for smaller datasets
   */
  private simpleReduction(waypoints: { lat: number; lng: number }[], maxPoints: number): { lat: number; lng: number }[] {
    const result: { lat: number; lng: number }[] = [];
    const step = Math.floor(waypoints.length / (maxPoints - 1));

    // Always include start point
    result.push(waypoints[0]);

    // Extract points at regular intervals, but also consider direction changes
    for (let i = step; i < waypoints.length - step; i += step) {
      const current = waypoints[i];
      
      // Check if this point represents a significant direction change
      if (this.isSignificantWaypoint(waypoints, i)) {
        result.push(current);
      } else {
        result.push(current);
      }
      
      if (result.length >= maxPoints - 1) break;
    }

    // Always include end point
    if (result.length === 0 || result[result.length - 1] !== waypoints[waypoints.length - 1]) {
      result.push(waypoints[waypoints.length - 1]);
    }

    return result;
  }

  /**
   * Simple heuristic to determine if a waypoint is significant
   */
  private isSignificantWaypoint(waypoints: { lat: number; lng: number }[], index: number): boolean {
    if (index === 0 || index === waypoints.length - 1) return true;
    
    const prev = waypoints[index - 1];
    const current = waypoints[index];
    const next = waypoints[index + 1];
    
    // Calculate bearing changes (simplified)
    const bearing1 = this.calculateBearing(prev.lat, prev.lng, current.lat, current.lng);
    const bearing2 = this.calculateBearing(current.lat, current.lng, next.lat, next.lng);
    
    const bearingDiff = Math.abs(bearing1 - bearing2);
    
    // Consider significant if direction changes more than 15 degrees
    return bearingDiff > 15;
  }

  /**
   * Calculate bearing between two points
   */
  private calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const lat1Rad = lat1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    
    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
    
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  }

  /**
   * Process GPX data through routing API to get optimized route
   */
  async processGPXThroughRoutingAPI(
    gpxData: GPXData, 
    profile: RouteProfile, 
    userId: string
  ): Promise<RouteProcessingResult> {
    try {
      // Step 1: Extract key waypoints with adaptive limits based on file size
      const maxWaypoints = this.calculateOptimalWaypointCount(gpxData.waypoints.length);
      const keyWaypoints = this.extractKeyWaypoints(gpxData.waypoints, maxWaypoints);
      
      // Step 2: Format waypoints for BRouter API (lng,lat format)
      const coordinatesString = keyWaypoints
        .map(point => `${point.lng},${point.lat}`)
        .join('|');

      // Step 3: Map bike profile to BRouter profile
      const profileParams = {
        road: 'fastbike',      // Use fastbike instead of car-fast
        trekking: 'trekking',
        gravel: 'gravel',      // Use gravel profile
        mtb: 'mtb'
      };

      const brouterProfile = profileParams[profile] || 'trekking';

      // Step 4: Call BRouter API through proxy
      const baseUrl = import.meta.env.DEV 
        ? 'http://localhost:3333/api'  // Development: direct to proxy
        : '/api';                      // Production: through nginx to proxy
      
      // Decide between GET and POST based on coordinate string length
      // URLs over ~2000 characters can cause "431 Request Header Fields Too Large" errors
      const usePost = coordinatesString.length > 1500; // Safe threshold
      
      let routingResponse: Response;
      
      if (usePost) {
        
        routingResponse = await fetch(`${baseUrl}/brouter`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lonlats: coordinatesString,
            profile: brouterProfile,
            alternativeidx: 0,
            format: 'geojson'
          })
        });
      } else {
        
        const routingUrl = `${baseUrl}/brouter?lonlats=${coordinatesString}&profile=${brouterProfile}&alternativeidx=0&format=geojson`;
        
        routingResponse = await fetch(routingUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        });
      }

      if (!routingResponse.ok) {
        const errorText = await routingResponse.text();
        throw new Error(`BRouter API failed: ${routingResponse.status} - ${errorText}`);
      }

      const routingResult = await routingResponse.json();      // Step 5: Parse BRouter API response (GeoJSON format)
      if (!routingResult.features || routingResult.features.length === 0) {
        throw new Error('No route found by BRouter API');
      }

      const feature = routingResult.features[0];
      const geometry = feature.geometry;
      const properties = feature.properties || {};

      if (!geometry || !geometry.coordinates) {
        throw new Error('Invalid route geometry from BRouter API');
      }

      // Step 6: Convert BRouter response to RoutePoints
      const routePoints: RoutePoint[] = geometry.coordinates.map((coord: number[], index: number) => ({
        lat: coord[1], // BRouter returns [lng, lat, elevation]
        lng: coord[0],
        orderIndex: index
      }));

      // Step 6b: Convert key waypoints to RoutePoints for storage
      // Store only the reduced waypoints, not BRouter's detailed response
      // Only resolve addresses for start and end points to minimize Nominatim API calls
      const storedRoutePoints: RoutePoint[] = await Promise.all(
        keyWaypoints.map(async (waypoint, index) => {
          const routePoint: RoutePoint = {
            lat: waypoint.lat,
            lng: waypoint.lng,
            orderIndex: index
          };

          // Only resolve addresses for start and end points to be respectful to Nominatim API
          const isStartOrEnd = index === 0 || index === keyWaypoints.length - 1;
          if (isStartOrEnd) {
            try {
              const geocodeResult = await reverseGeocode({ lat: waypoint.lat, lng: waypoint.lng });
              const addressFields = geocodeResultToAddressFields(geocodeResult);
              Object.assign(routePoint, addressFields);
            } catch (error) {
              console.warn(`Failed to resolve address for ${index === 0 ? 'start' : 'end'} point:`, error);
              // Continue without address data - not critical for route functionality
            }
          }

          return routePoint;
        })
      );

      // Step 7: Create Route object with GPX metadata
      const route: Route = {
        name: gpxData.name,
        bikeProfile: profile,
        userId: userId,
        points: storedRoutePoints, // Store only key waypoints, not BRouter's detailed response
        distance: properties['track-length'] || this.calculateDistance(routePoints), // Use BRouter's calculated distance
        duration: properties['total-time'] || this.estimateDuration(routePoints, profile), // Use BRouter's calculated duration
        ascend: properties['filtered ascend'] || 0, // BRouter elevation gain in meters
        sourceType: 'gpx_optimized',
        originalFilename: gpxData.metadata.filename,
        gpxMetadata: {
          filename: gpxData.metadata.filename,
          trackCount: gpxData.metadata.trackCount,
          totalPoints: gpxData.metadata.totalPoints,
          deviceInfo: gpxData.metadata.deviceInfo,
          timestamps: gpxData.metadata.timestamps,
          originalBounds: gpxData.metadata.bounds
        }
      };

      return {
        success: true,
        route
      };

    } catch (error) {
      console.error('GPX processing failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to process GPX file'
      };
    }
  }

  /**
   * Calculate distance between route points (Haversine formula)
   */
  private calculateDistance(points: RoutePoint[]): number {
    if (points.length < 2) return 0;
    
    let totalDistance = 0;
    
    for (let i = 1; i < points.length; i++) {
      const lat1 = points[i - 1].lat * Math.PI / 180;
      const lon1 = points[i - 1].lng * Math.PI / 180;
      const lat2 = points[i].lat * Math.PI / 180;
      const lon2 = points[i].lng * Math.PI / 180;
      
      const dLat = lat2 - lat1;
      const dLon = lon2 - lon1;
      
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = 6371000 * c; // Earth's radius in meters
      
      totalDistance += distance;
    }
    
    return Math.round(totalDistance);
  }

  /**
   * Calculate distance between two individual points (Haversine formula)
   */
  private calculateDistanceBetweenPoints(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const lat1Rad = lat1 * Math.PI / 180;
    const lon1Rad = lng1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    const lon2Rad = lng2 * Math.PI / 180;
    
    const dLat = lat2Rad - lat1Rad;
    const dLon = lon2Rad - lon1Rad;
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceKm = 6371 * c; // Earth's radius in kilometers
    
    return distanceKm;
  }

  /**
   * Estimate duration based on distance and bike profile
   */
  private estimateDuration(points: RoutePoint[], profile: RouteProfile): number {
    const distance = this.calculateDistance(points);
    const distanceKm = distance / 1000;
    
    // Average speeds by profile (km/h)
    const speeds = {
      road: 25,
      trekking: 20,
      gravel: 18,
      mtb: 15
    };
    
    const speed = speeds[profile] || speeds.trekking;
    return Math.round((distanceKm / speed) * 3600); // Convert to seconds
  }

  /**
   * Fallback method: Import GPX directly without routing optimization
   * Used when routing API fails
   */
  async importGPXDirectly(gpxData: GPXData, profile: RouteProfile, userId: string): Promise<RouteProcessingResult> {
    try {
      // Reduce waypoints to manageable number for storage
      const reducedWaypoints = this.extractKeyWaypoints(gpxData.waypoints, 100);
      
      // Create route points - only resolve addresses for start and end points to minimize API calls
      const routePoints: RoutePoint[] = await Promise.all(
        reducedWaypoints.map(async (point, index) => {
          const routePoint: RoutePoint = {
            lat: point.lat,
            lng: point.lng,
            orderIndex: index
          };

          // Only resolve addresses for start and end points to be respectful to Nominatim API
          const isStartOrEnd = index === 0 || index === reducedWaypoints.length - 1;
          if (isStartOrEnd) {
            try {
              const geocodeResult = await reverseGeocode({ lat: point.lat, lng: point.lng });
              const addressFields = geocodeResultToAddressFields(geocodeResult);
              Object.assign(routePoint, addressFields);
            } catch (error) {
              console.warn(`Failed to resolve address for ${index === 0 ? 'start' : 'end'} point:`, error);
              // Continue without address data - not critical for route functionality
            }
          }

          return routePoint;
        })
      );

      const route: Route = {
        name: gpxData.name,
        bikeProfile: profile,
        userId: userId,
        points: routePoints,
        distance: this.calculateDistance(routePoints),
        duration: this.estimateDuration(routePoints, profile),
        ascend: 0, // Can't calculate elevation without elevation data
        sourceType: 'gpx_import', // Direct import, not optimized
        originalFilename: gpxData.metadata.filename,
        gpxMetadata: {
          filename: gpxData.metadata.filename,
          trackCount: gpxData.metadata.trackCount,
          totalPoints: gpxData.metadata.totalPoints,
          deviceInfo: gpxData.metadata.deviceInfo,
          timestamps: gpxData.metadata.timestamps,
          originalBounds: gpxData.metadata.bounds
        }
      };

      return {
        success: true,
        route
      };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to import GPX file directly'
      };
    }
  }
}

export const gpxProcessor = new GPXProcessor();
